import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "../stores/authStore.js";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "Login",
      component: () => import("../views/Login.vue"),
    },
    {
      path: "/",
      // Avoid dashboard flash: decide destination based on current auth session.
      // If there's no token, go straight to login.
      redirect: () => {
        try {
          const auth = useAuthStore();
          return auth.hasToken ? "/dashboard" : "/login";
        } catch (e) {
          return "/login";
        }
      },
    },
    {
      path: "/dashboard",
      name: "Dashboard",
      component: () => import("../views/Dashboard.vue"),
    },
    {
      path: "/companies",
      name: "Companies",
      component: () => import("../views/Companies.vue"),
    },
    {
      path: "/branches",
      name: "Branches",
      component: () => import("../views/Branches.vue"),
    },
    {
      path: "/product-management",
      name: "ProductManagement",
      component: () => import("../views/ProductManagement.vue"),
    },
    {
      path: "/tax-invoices",
      name: "TaxInvoices",
      component: () => import("../views/TaxInvoicesNew.vue"),
    },
    // 🚫 DISABLED: Tax Invoice (Old) - เก็บไว้ใช้ในอนาคต
    // Component backup: TaxInvoice.vue.backup
    // {
    //   path: '/tax-invoice',
    //   name: 'TaxInvoice',
    //   component: () => import('../views/TaxInvoice.vue')
    // },
    // 🚫 DISABLED: Tax Invoices (New) - เก็บไว้ใช้ในอนาคต
    // Component backup: TaxInvoices.vue.backup
    // {
    //   path: '/invoices/tax',
    //   name: 'TaxInvoices',
    //   component: () => import('../views/TaxInvoices.vue')
    // },
    {
      path: "/invoices/payment",
      name: "PaymentReceipts",
      component: () => import("../views/PaymentReceipts.vue"),
    },
    // 🚫 DISABLED: Payment Voucher Form - ไฟล์ถูกลบเนื่องจากมีข้อผิดพลาด
    // {
    //   path: '/payment-voucher',
    //   name: 'payment-voucher',
    //   component: () => import('../views/PaymentVoucherForm.vue')
    // },
    {
      path: "/invoices/withholding",
      name: "WithholdingTaxReceipts",
      component: () => import("../views/WithholdingTaxReceipts.vue"),
    },
    {
      path: "/invoices/withholding/create",
      name: "WithholdingTaxForm",
      component: () => import("../views/WithholdingTaxFormV2.vue"),
    },
    {
      path: "/document-history",
      name: "DocumentHistory",
      component: () => import("../views/TaxInvoiceHistory.vue"),
    },
    {
      path: "/customers",
      name: "Customers",
      component: () => import("../views/Customers.vue"),
    },
    {
      path: "/quotations",
      name: "Quotations",
      component: () => import("../views/Quotations.vue"),
    },
    {
      path: "/products",
      name: "Products",
      component: () => import("../views/ProductManagement.vue"),
    },
    {
      path: "/categories",
      name: "Categories",
      component: () => import("../views/Categories.vue"),
    },
    {
      path: "/users",
      name: "Users",
      component: () => import("../views/Users.vue"),
    },
    {
      path: "/users/permissions",
      name: "RolePermissions",
      component: () => import("../views/RolePermissions.vue"),
    },
    {
      path: "/employees",
      name: "Employees",
      component: () => import("../views/Employees.vue"),
    },
    {
      path: "/employee-payroll",
      name: "EmployeePayroll",
      component: () => import("../views/EmployeePayroll.vue"),
    },
    {
      path: "/employee-withholding-tax",
      name: "EmployeeWithholdingTax",
      component: () => import("../views/EmployeeWithholdingTax.vue"),
    },
    // 🆕 Payment Vouchers V2 (VEN + SAL + WHT integration)
    {
      path: "/payment-vouchers",
      name: "PaymentVouchersV2",
      component: () => import("../views/PaymentVouchersV2.vue"),
    },
    // 🆕 Reports System (รายงานต่างๆ)
    {
      path: "/reports",
      name: "Reports",
      component: () => import("../views/Reports.vue"),
    },
    {
      path: "/reports/tax-invoices",
      name: "TaxInvoiceReport",
      component: () => import("../views/TaxInvoiceReport.vue"),
    },
    {
      path: "/reports/withholding-tax",
      name: "WHTReport",
      component: () => import("../views/WHTReport.vue"),
    },
    {
      path: "/reports/payroll",
      name: "PayrollReport",
      component: () => import("../views/PayrollReport.vue"),
    },
    {
      path: "/signature-management",
      name: "SignatureManagement",
      component: () => import("../components/SignatureManagement.vue"),
      meta: { requiresAuth: true },
    },
  ],
});

// Optional: light guard to keep /login for unauthenticated users; other routes remain accessible in Phase 1
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore();

  // Public routes
  const publicPaths = ["/login"];
  const isPublic = publicPaths.includes(to.path);

  // 🔄 Selective cache clear for invoice endpoints only (ลด side effects, คืนเสถียรภาพโหลดแรก)
  if (from.name && to.name !== from.name) {
    console.log(`🔄 [Router] Navigation: ${from.name} → ${to.name} (invoice cache sweep)`);
    if ("caches" in window) {
      try {
        const cache = await caches.open("api-cache");
        const targets = [
          "/api/invoices?type=payment_receipt",
          "/api/invoices?type=tax_invoice",
          "/api/invoices?type=withholding_tax_receipt",
        ];
        await Promise.all(targets.map((t) => cache.delete(t)));
        console.log("✅ [Router] Cleared invoice caches:", targets);
      } catch (e) {
        console.warn("⚠️ [Router] Invoice cache clear failed:", e.message);
      }
    }
  }

  // Validate session before entering protected pages.
  // If token exists but /auth/me fails, clear token and force login.
  if (auth.token && !auth.user) {
    const ok = await auth.fetchMe();
    if (!ok) {
      if (!isPublic) {
        return next({ path: "/login", query: { redirect: to.fullPath } });
      }
    }
  }

  if (!isPublic && !auth.isAuthenticated) {
    return next({ path: "/login", query: { redirect: to.fullPath } });
  }
  if (to.path === "/login" && auth.isAuthenticated) {
    return next("/dashboard");
  }
  next();
});

export default router;
